import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Card, Col, InputNumber, Modal, Row, Space, Statistic, Switch } from 'antd'
import PageLoader from '../../components/Layouts/PageLoader'
import {
  errorNotification,
  getRequest,
  putRequest,
  successNotification,
  trackingPricingUrl
} from '../../config'
import {
  accountProblems,
  buildPricePayload,
  errorSentence,
  money,
  toMoneyString,
  validatePrice
} from './utils'

const yesNo = value => (value ? 'yes' : 'no')

// What a customer pays for tracking, and — more importantly — whether they can buy it at all. A
// price switched off keeps its figure and sells nothing, so this page is where a silent outage
// would come from; it says so in red rather than leaving the switch to speak for itself.
const PricingPage = () => {
  const [loading, setLoading] = useState(false)
  const [pricing, setPricing] = useState(null)
  const [amount, setAmount] = useState(null)
  const [errors, setErrors] = useState([])
  const [needsAccount, setNeedsAccount] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    const response = await getRequest(trackingPricingUrl('GH'), 'price', setLoading)
    if (!response.success) {
      errorNotification(errorSentence(response, 'The price could not be loaded.'))
      return
    }
    // The endpoint never 404s: "no price yet" arrives as `amount: null` with `active: false`.
    setPricing(response.data)
    setAmount(response.data.amount == null ? null : Number(response.data.amount))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  const send = async (payload, done) => {
    // One PUT per confirm: the price is a figure the whole app reads, and two in flight is a race.
    if (saving) return
    const response = await putRequest(trackingPricingUrl('GH'), payload, 'price', setSaving)
    if (response.success) {
      successNotification(done)
      setErrors([])
      setNeedsAccount(false)
      await load()
      return
    }
    const sentence = errorSentence(response, 'The price could not be saved.')
    setErrors([sentence])
    // ShipsGo has to work before tracking can be sold, and that is fixed on the account page.
    setNeedsAccount(response.error?.response?.data?.code === 'shipsgo_not_configured')
    errorNotification(sentence)
    // Re-read rather than leave the typed figure looking saved.
    await load()
  }

  const save = () => {
    const problems = validatePrice(amount)
    setErrors(problems)
    if (problems.length) return
    Modal.confirm({
      title: `Set the price to GHS ${toMoneyString(amount)}?`,
      content:
        'New purchases pay the new price. Anyone who has already reached the pay screen keeps the price that was locked for them. Setting a price also puts it on sale.',
      okText: 'Set price',
      onOk: () => send(buildPricePayload(amount, true), 'Price saved.')
    })
  }

  const toggleSale = next => {
    if (next) {
      Modal.confirm({
        title: `Start selling at ${money(pricing.amount, pricing.currency)}?`,
        content: 'Customers can buy Shipment tracking again at this price.',
        okText: 'Start selling',
        onOk: () => send(buildPricePayload(pricing.amount, true), 'Now on sale.')
      })
      return
    }
    Modal.confirm({
      title: 'Stop selling Shipment tracking?',
      content:
        'Customers will see \'not available yet\'. The price is kept and existing shipments are not affected.',
      okText: 'Stop selling',
      okType: 'danger',
      onOk: () => send(buildPricePayload(pricing.amount, false), 'Selling switched off.')
    })
  }

  // The same sentence the worklist and detail banners show, from the one place that writes it.
  const noPrice = (accountProblems(null, pricing) || []).find(problem => problem.key === 'no_price')

  return (
    <PageLoader className="container" loading={loading}>
      <Card title="Shipment tracking price — Ghana">
        {noPrice ? (
          <Alert type="error" showIcon style={{ marginBottom: 16 }} message={noPrice.message} />
        ) : null}

        {errors.length ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                {errors.map(message => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            }
            action={needsAccount ? <Link to="/tracking/account">ShipsGo account</Link> : null}
          />
        ) : null}

        {pricing ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Statistic
                title="Customers pay"
                value={pricing.amount == null ? '—' : money(pricing.amount, pricing.currency)}
              />
              {/* Hidden until there is a price: there is nothing to put on or off sale yet. */}
              {pricing.amount != null ? (
                <div style={{ marginTop: 12 }}>
                  <Space>
                    <Switch
                      checked={pricing.active === true}
                      onChange={toggleSale}
                      loading={saving}
                    />
                    <span>On sale</span>
                  </Space>
                </div>
              ) : null}
              <div style={{ marginTop: 12, opacity: 0.65 }}>
                <div>{`Available to customers: ${yesNo(pricing.available)}`}</div>
                <div>
                  {`ShipsGo ready: ${yesNo(pricing.shipsgoReady)}`}
                  {pricing.shipsgoReady === false ? (
                    <span> · <Link to="/tracking/account">ShipsGo account</Link></span>
                  ) : null}
                </div>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <label style={{ display: 'block' }}>New price (GHS)</label>
              <InputNumber
                min={0}
                step={1}
                precision={2}
                style={{ width: '100%' }}
                value={amount}
                onChange={value => setAmount(value)}
              />
              <div style={{ marginTop: 12 }}>
                <Button type="primary" loading={saving} onClick={save}>
                  Save
                </Button>
              </div>
              <div style={{ marginTop: 12, opacity: 0.65 }}>
                The price is locked onto each purchase when the customer reaches the pay screen.
                Changing it never changes what someone has already been asked to pay.
              </div>
            </Col>
          </Row>
        ) : null}
      </Card>
    </PageLoader>
  )
}

export default PricingPage
